import { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import TableWrapper from '@/components/atoms/TableWrapper';
import CustomTable from '@/components/atoms/CustomTable';
import { Filters } from './Filters';
import {
  setUsersFilters,
  useUsersFilters,
} from '@/store/slices/reportFiltersSlice';
import { RequestDetailsDrawer } from './RequestDetailsDrawer';
import { useGetUserssReportQuery } from '@/store/services/reports';
import {
  setModalId,
  setPage,
  usePublicState,
} from '@/store/slices/publicSlice';

export interface UserProfileItem {
  joinDate: string;
  name: string;
  profileId: string;
  userEmail: string;
  userName: string;
  userSerial: string | null;
  userType: string;
}

const RequestsTable = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { search, page } = usePublicState();
  const filters = useUsersFilters();
  const dispatch = useDispatch();

  const searchValue = search?.usersReportSearch || '';

  const {
    data: usersList,
    isLoading,
    isFetching,
  } = useGetUserssReportQuery(
    {
      page: page,
      search: searchValue,
      dateFrom: filters?.fromDate || '',
      dateTo: filters?.toDate || '',
      customerType: filters?.profileTypes,
      location: filters?.location || '',
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !filters?.fromDate && !filters?.toDate,
    }
  );

  const columns: TableProps<UserProfileItem>['columns'] = [
    {
      title: t('reports.users.userId', 'User ID'),
      dataIndex: 'userId',
      key: 'userId',
      render: (val) => val || '-',
    },
    {
      title: t('reports.users.username', 'Username'),
      dataIndex: 'username',
      key: 'username',
      render: (val) => val || '-',
    },
    {
      title: t('reports.users.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (val) => val || '-',
    },

    {
      title: t('reports.customerType', 'Customer type'),
      dataIndex: 'customerType',
      key: 'customerType',
      render: (val) => val || '-',
    },

    {
      title: t('reports.joinDate', 'Join date'),
      key: 'joinDate',
      dataIndex: 'joinDate',
      render: (value) => {
        return (
          <p>
            {dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : '-'}
          </p>
        );
      },
    },

    {
      title: t('reports.users.mail', 'User email'),
      dataIndex: 'email',
      key: 'email',
      render: (val) => val || '-',
    },
  ];

  dayjs.locale(language);

  return (
    <>
      <TableWrapper
        filters={<Filters />}
        title={t('reports.users.usersList', 'Users lists')}
        subTitle={t(
          'reports.users.usersListSubTitle',
          'A complete directory of all platform users'
        )}
      >
        <CustomTable
          loading={isLoading || isFetching}
          currentPage={page ?? 0}
          pageSize={filters?.limit ?? 10}
          totalElements={(usersList?.payload as any)?.totalElements ?? 0}
          onPageChange={(pageNumber, pageSize) => {
            dispatch(setPage(pageNumber - 1));
            dispatch(
              setUsersFilters({
                limit: pageSize,
              })
            );
          }}
          onRowClick={(_, record: any) => {
            const id = record.id;
            dispatch(setModalId({ id }));
          }}
          columns={columns as any}
          dataSource={
            usersList?.payload?.content?.map(
              (item: UserProfileItem, idx: number) => ({
                key: item?.profileId || idx,
                id: item?.profileId,
                userId: item?.userSerial,
                name: item?.name,
                username: item?.userName,
                email: item?.userEmail,
                customerType: item?.userType,
                joinDate: item?.joinDate,
              })
            ) || []
          }
        />
      </TableWrapper>

      <RequestDetailsDrawer
        listOfIds={
          usersList?.payload?.content?.map((el) => el.profileId) as string[]
        }
      />
    </>
  );
};

export default RequestsTable;
